// The fake api's stock routes (spec/13 §6.4, ADR-0040; `packages/contracts/src/routes/inventory.ts`), in the
// contract's shapes and with its rules — enough for the Stock screens, the product dialog's recipes, "What needs you"
// and the stock reports to be rendered and tested against `fake-server.ts`:
//
//   GET  …/inventory/status | alerts                                 inventory:read
//   GET/POST …/inventory/items, GET/PATCH …/items/{id}, GET …/lookup inventory:read / inventory:manage (supplies narrow)
//   GET  …/inventory/movements                                       inventory:read, cursor-paginated
//   GET/POST …/inventory/suppliers, PATCH …/suppliers/{id}           inventory:read / inventory:manage
//   GET/POST …/inventory/receipts, GET …/receipts/{id}               inventory:read / inventory:receive
//   POST …/inventory/usage | adjustments                             inventory:waste / inventory:adjust
//   GET/POST …/inventory/counts, GET …/counts/{id}, PUT …/lines, POST …/submit | cancel   inventory:read / count
//   GET  …/inventory/recipes, GET/PUT …/products/{p}/recipes         inventory:read / inventory:manage
//   POST …/inventory/copy-from-venue                                 inventory:manage
//   GET  …/inventory/reports/valuation | usage | variance | margins  inventory:reports
//
// What it keeps faithfully: every quantity is a whole number of base units and on hand is the sum of the movements;
// a balance is a (quantity, value) pair and a movement's value is rounded once, half away from zero, by ADR-0040 §15's
// rules (out at the average, a receipt adds exactly its cost, a receipt into negative stock revalues it at the
// purchase price, zero is worth zero); the ₹ figures are `null` without `inventory:reports`; a caller holding
// `inventory:supplies` and not the route's own permission is held to supply items; and every write is refused
// `409 inventory_disabled` while stock is off at the venue (the default, as `inventory.enabled` is).
//
// The venue (Playground Central) runs a café menu from `fake-venue-commerce.ts`: fries, sandwiches, biryani, cold
// coffee in two sizes, chai and cans. Chicken has sold below zero ("Recount needed"), paneer is out, five items are
// low, the peri-peri masala was only ever counted (so it has no cost and Masala fries' cost is incomplete), and the
// paneer sandwich and the biryani are sold out by stock. Playground North (the member's second venue) has its own
// items to copy from.

import type {
  Money,
  Problem,
  StockBaseUnit,
  StockCount,
  StockCountLine,
  StockItem,
  StockItemKind,
  StockItemPack,
  StockLevel,
  StockMovement,
  StockMovementKind,
  StockReceipt,
  StockRecipe,
  StockSupplier,
  StockUnitCost,
} from '@clavian/api-client';
import {
  CopyStockFromVenueSchema,
  CreateStockAdjustmentSchema,
  CreateStockCountSchema,
  CreateStockItemSchema,
  CreateStockReceiptSchema,
  CreateStockSupplierSchema,
  CreateStockUsageSchema,
  PutStockCountLinesSchema,
  PutStockProductRecipesSchema,
  UpdateStockItemSchema,
  UpdateStockSupplierSchema,
  type z,
} from '@clavian/contracts';
import { priceFoodOrderLine, type FoodTaxTreatment } from '@clavian/domain';

import { COMMERCE_PRODUCTS } from './fake-venue-commerce';
import type { FakeResponse } from './fake-server';

const ORGANIZATION_ID = 'org_01k5znw4hmfay8vvr14d2pf2db';
const VENUE_ID = 'ven_01k5znw4hmfay8vvr14d2pf2dc';
const SECOND_VENUE_ID = 'ven_01k5znw4hmfay8vvr14d2pf2dd';
const CURRENCY = 'INR';
/** The fixtures' "now": 26 September, 19:30 at the venue. */
export const INVENTORY_NOW = '2026-09-26T14:00:00.000Z';
/** Who the console's writes are recorded as: the fake's signed-in member. */
const RECORDER = 'Riya Sharma';
/** A `regular` venue with inclusive prices, as the commerce fake prices its sales. */
const TREATMENT: FoodTaxTreatment = { charges: true, pricing: 'inclusive', place: 'intra_state' };

const id = (prefix: string, tail: string): string => `${prefix}_01k5znw4hmfay8vvr14d2pf${tail}`;
const numberedId = (prefix: string, serial: number): string => `${prefix}_01k5znw4hmfay8vvr14d2${String(serial).padStart(5, '0')}`;
const inr = (amount: number): Money => ({ amount, currency: CURRENCY });
const productId = (tail: string) => id('prd', tail);
const at = (date: string, time: string) => new Date(`${date}T${time}:00+05:30`).toISOString();

// ---------------------------------------------------------------------------------------------
// Valuation (ADR-0040 §15): exact pairs, one rounding per movement
// ---------------------------------------------------------------------------------------------

/** `numerator ÷ denominator`, rounded half away from zero (denominator positive). */
function round(numerator: bigint, denominator: bigint): number {
  const negative = numerator < 0n;
  const magnitude = negative ? -numerator : numerator;
  const quotient = (magnitude * 2n + denominator) / (denominator * 2n);
  return Number(negative ? -quotient : quotient);
}

interface Balance {
  quantity: number;
  value: number;
  /** The last positive average (or a receipt into negative stock's price): `value` for `quantity` base units. */
  basis: { value: number; quantity: number } | null;
}

/** The value `q` base units leaving `(Q, V)` take with them. */
function valueOut(balance: Balance, q: number): number {
  const { quantity: Q, value: V } = balance;
  if (Q > 0) {
    if (q < Q) return round(BigInt(q) * BigInt(V), BigInt(Q));
    if (q === Q) return V;
    return V + round(BigInt(q - Q) * BigInt(V), BigInt(Q));
  }
  return balance.basis === null ? 0 : round(BigInt(q) * BigInt(balance.basis.value), BigInt(balance.basis.quantity));
}

/** The value `q` base units coming back in (a refund, a count or an adjustment up) bring, at the average. */
function valueIn(balance: Balance, q: number): number {
  if (balance.quantity > 0) return round(BigInt(q) * BigInt(balance.value), BigInt(balance.quantity));
  return balance.basis === null ? 0 : round(BigInt(q) * BigInt(balance.basis.value), BigInt(balance.basis.quantity));
}

/** Apply a signed change and its value; zero means zero, and a positive average becomes the basis. */
function apply(balance: Balance, change: number, value: number): void {
  balance.quantity += change;
  balance.value += value;
  if (balance.quantity === 0) balance.value = 0;
  if (balance.quantity > 0) balance.basis = { value: balance.value, quantity: balance.quantity };
}

/** A receipt of `q` for `cost`: the value rises by exactly the cost, or, into negative stock, is revalued at it. */
function receive(balance: Balance, q: number, cost: number): number {
  const before = balance.value;
  if (balance.quantity >= 0) {
    balance.quantity += q;
    balance.value += cost;
  } else {
    balance.quantity += q;
    balance.value = round(BigInt(balance.quantity) * BigInt(cost), BigInt(q));
    if (q > 0) balance.basis = { value: cost, quantity: q };
  }
  if (balance.quantity === 0) balance.value = 0;
  if (balance.quantity > 0) balance.basis = { value: balance.value, quantity: balance.quantity };
  return balance.value - before;
}

/** The average to value at: the balance's own while positive, else its basis; null for an item never costed. */
function unitCostOf(balance: Balance): StockUnitCost | null {
  if (balance.quantity > 0 && balance.value > 0) return { amount: inr(balance.value), quantity: balance.quantity };
  return balance.basis === null || balance.basis.value === 0 ? null : { amount: inr(balance.basis.value), quantity: balance.basis.quantity };
}

// ---------------------------------------------------------------------------------------------
// The seeded venue
// ---------------------------------------------------------------------------------------------

interface ItemRecord {
  readonly id: string;
  name: string;
  readonly kind: StockItemKind;
  baseUnit: StockBaseUnit;
  sku: string | null;
  barcode: string | null;
  readonly productId: string | null;
  parLevel: number | null;
  note: string | null;
  /** What one `each` unit is called, singular and plural ("can", "cans"); null for none. */
  unitName: string | null;
  unitNamePlural: string | null;
  supplierId: string | null;
  packs: StockItemPack[];
  balance: Balance;
  lastMovementAt: string | null;
  lastCountedAt: string | null;
  archived: boolean;
  readonly createdAt: string;
  updatedAt: string;
  /** The source item a copy made this from, so a second copy skips it (ADR-0040 §9). */
  copiedFrom?: string;
}

interface Seed {
  readonly tail: string;
  readonly name: string;
  readonly kind: StockItemKind;
  readonly baseUnit: StockBaseUnit;
  readonly packs?: readonly { tail: string; name: string; quantity: number; barcode?: string }[];
  readonly par: number | null;
  readonly onHand: number;
  /** Paise for `per` base units at the item's average: ₹72/kg is `{ paise: 7200, per: 1000 }`. Absent: never costed. */
  readonly cost?: { readonly paise: number; readonly per: number };
  readonly sku?: string;
  readonly barcode?: string;
  readonly productTail?: string;
  readonly archived?: boolean;
  readonly note?: string;
  /** What one is called, singular and plural, for an `each` item. */
  readonly unit?: readonly [string, string];
  /** The usual supplier's tail (`s01`…). */
  readonly supplier?: string;
}

const SEEDS: readonly Seed[] = [
  { tail: 'k01', name: 'Frozen fries', kind: 'ingredient', baseUnit: 'g', packs: [{ tail: 'k01', name: '2.5 kg bag', quantity: 2500 }], par: 5000, onHand: 3200, cost: { paise: 7200, per: 1000 }, sku: 'FRZ-FRIES', supplier: 's01' },
  { tail: 'k02', name: 'Peri-peri masala', kind: 'ingredient', baseUnit: 'g', packs: [{ tail: 'k02', name: '500 g pouch', quantity: 500 }], par: 250, onHand: 380, note: 'Counted in, never bought: no cost yet.' },
  { tail: 'k03', name: 'Cheese slices', kind: 'ingredient', baseUnit: 'each', packs: [{ tail: 'k03', name: 'Pack of 100', quantity: 100, barcode: '8901262150101' }], par: 50, onHand: 32, cost: { paise: 450, per: 1 }, unit: ['slice', 'slices'] },
  { tail: 'k04', name: 'Curd', kind: 'ingredient', baseUnit: 'g', packs: [{ tail: 'k04', name: '1 kg tub', quantity: 1000 }], par: 1000, onHand: 2200, cost: { paise: 8000, per: 1000 }, supplier: 's02' },
  { tail: 'k05', name: 'Milk', kind: 'ingredient', baseUnit: 'ml', packs: [{ tail: 'k05', name: '1 L packet', quantity: 1000 }], par: 6000, onHand: 8500, cost: { paise: 5600, per: 1000 }, supplier: 's02' },
  { tail: 'k06', name: 'Coffee beans', kind: 'ingredient', baseUnit: 'g', packs: [{ tail: 'k06', name: '1 kg bag', quantity: 1000 }], par: 1000, onHand: 2350, cost: { paise: 90_000, per: 1000 } },
  { tail: 'k07', name: 'Tea leaves', kind: 'ingredient', baseUnit: 'g', packs: [{ tail: 'k07', name: '500 g pack', quantity: 500 }], par: 300, onHand: 620, cost: { paise: 30_000, per: 1000 } },
  { tail: 'k08', name: 'Sugar', kind: 'ingredient', baseUnit: 'g', packs: [{ tail: 'k08', name: '5 kg bag', quantity: 5000 }], par: 2000, onHand: 6400, cost: { paise: 4500, per: 1000 }, supplier: 's01' },
  { tail: 'k09', name: 'Boneless chicken', kind: 'ingredient', baseUnit: 'g', packs: [{ tail: 'k09', name: '1 kg pack', quantity: 1000 }], par: 3000, onHand: -400, cost: { paise: 32_000, per: 1000 } },
  { tail: 'k10', name: 'Basmati rice', kind: 'ingredient', baseUnit: 'g', packs: [{ tail: 'k10', name: '5 kg bag', quantity: 5000 }], par: 5000, onHand: 12_500, cost: { paise: 11_000, per: 1000 }, supplier: 's01' },
  { tail: 'k11', name: 'Paneer', kind: 'ingredient', baseUnit: 'g', packs: [{ tail: 'k11', name: '200 g block', quantity: 200 }], par: 1000, onHand: 0, cost: { paise: 35_000, per: 1000 } },
  { tail: 'k12', name: 'Sandwich bread', kind: 'ingredient', baseUnit: 'each', packs: [{ tail: 'k12', name: 'Loaf of 20 slices', quantity: 20 }], par: 20, onHand: 46, cost: { paise: 225, per: 1 }, unit: ['slice', 'slices'] },
  { tail: 'k13', name: 'Eggs', kind: 'ingredient', baseUnit: 'each', packs: [{ tail: 'k13', name: 'Tray of 30', quantity: 30 }], par: 30, onHand: 54, cost: { paise: 650, per: 1 }, unit: ['egg', 'eggs'] },
  { tail: 'k14', name: 'Cola can 330 ml', kind: 'ingredient', baseUnit: 'each', packs: [{ tail: 'k14', name: 'Case of 24', quantity: 24, barcode: '8901764012273' }], par: 24, onHand: 40, cost: { paise: 2800, per: 1 }, barcode: '8901764012211', unit: ['can', 'cans'], supplier: 's03' },
  { tail: 'k15', name: 'Chicken nuggets', kind: 'ingredient', baseUnit: 'each', packs: [{ tail: 'k15', name: 'Box of 50', quantity: 50 }], par: 60, onHand: 72, cost: { paise: 1200, per: 1 }, unit: ['nugget', 'nuggets'] },
  { tail: 'k16', name: 'Chocolate brownie', kind: 'ingredient', baseUnit: 'each', packs: [{ tail: 'k16', name: 'Tray of 12', quantity: 12 }], par: 12, onHand: 9, cost: { paise: 3800, per: 1 }, unit: ['brownie', 'brownies'] },
  { tail: 'k20', name: 'Energy drink 250 ml', kind: 'retail', baseUnit: 'each', packs: [{ tail: 'k20', name: 'Case of 24', quantity: 24, barcode: '8906000000106' }], par: 12, onHand: 18, cost: { paise: 6500, per: 1 }, barcode: '8906000000014', productTail: 'r01', unit: ['can', 'cans'], supplier: 's03' },
  { tail: 'k21', name: 'Mouse pad', kind: 'retail', baseUnit: 'each', par: 5, onHand: 7, cost: { paise: 18_000, per: 1 }, barcode: '8906000000021', productTail: 'r02', unit: ['pad', 'pads'] },
  { tail: 'k22', name: 'Salted peanuts 50 g', kind: 'retail', baseUnit: 'each', packs: [{ tail: 'k22', name: 'Box of 30', quantity: 30 }], par: 20, onHand: 26, cost: { paise: 1500, per: 1 }, barcode: '8906000000038', productTail: 'r03', unit: ['packet', 'packets'] },
  { tail: 'k30', name: 'Floor cleaner', kind: 'supply', baseUnit: 'ml', packs: [{ tail: 'k30', name: '5 L can', quantity: 5000 }], par: 5000, onHand: 3500, cost: { paise: 42_000, per: 5000 } },
  { tail: 'k31', name: 'Spare keyboard', kind: 'supply', baseUnit: 'each', par: 2, onHand: 3, cost: { paise: 145_000, per: 1 }, unit: ['keyboard', 'keyboards'], supplier: 's04' },
  { tail: 'k32', name: 'HDMI cable 2 m', kind: 'supply', baseUnit: 'each', par: 2, onHand: 1, cost: { paise: 35_000, per: 1 }, unit: ['cable', 'cables'], supplier: 's04' },
  { tail: 'k33', name: 'Paper cups 250 ml', kind: 'supply', baseUnit: 'each', packs: [{ tail: 'k33', name: 'Sleeve of 50', quantity: 50 }], par: 100, onHand: 240, cost: { paise: 180, per: 1 }, unit: ['cup', 'cups'] },
  { tail: 'k34', name: 'Garlic mayo sachets', kind: 'ingredient', baseUnit: 'each', par: null, onHand: 0, archived: true },
];

/** Playground North's own items: what a copy brings across (definitions only). Eight share a name with ours. */
const SOURCE_SEEDS: readonly Seed[] = [
  { tail: 'n01', name: 'Frozen fries', kind: 'ingredient', baseUnit: 'g', packs: [{ tail: 'n01', name: '2.5 kg bag', quantity: 2500 }], par: 4000, onHand: 5100, cost: { paise: 7400, per: 1000 } },
  { tail: 'n02', name: 'Peri-peri masala', kind: 'ingredient', baseUnit: 'g', par: 200, onHand: 300 },
  { tail: 'n03', name: 'Cheese slices', kind: 'ingredient', baseUnit: 'each', packs: [{ tail: 'n03', name: 'Pack of 100', quantity: 100 }], par: 40, onHand: 80, unit: ['slice', 'slices'] },
  { tail: 'n04', name: 'Milk', kind: 'ingredient', baseUnit: 'ml', packs: [{ tail: 'n04', name: '1 L packet', quantity: 1000 }], par: 5000, onHand: 9000 },
  { tail: 'n05', name: 'Coffee beans', kind: 'ingredient', baseUnit: 'g', packs: [{ tail: 'n05', name: '1 kg bag', quantity: 1000 }], par: 1000, onHand: 1800 },
  { tail: 'n06', name: 'Sugar', kind: 'ingredient', baseUnit: 'g', packs: [{ tail: 'n06', name: '5 kg bag', quantity: 5000 }], par: 2000, onHand: 4000 },
  { tail: 'n07', name: 'Burger buns', kind: 'ingredient', baseUnit: 'each', packs: [{ tail: 'n07', name: 'Pack of 6', quantity: 6 }], par: 24, onHand: 30, unit: ['bun', 'buns'] },
  { tail: 'n08', name: 'Veg patty', kind: 'ingredient', baseUnit: 'each', packs: [{ tail: 'n08', name: 'Box of 20', quantity: 20 }], par: 20, onHand: 36, unit: ['patty', 'patties'] },
  { tail: 'n09', name: 'Hakka noodles', kind: 'ingredient', baseUnit: 'g', packs: [{ tail: 'n09', name: '1 kg pack', quantity: 1000 }], par: 2000, onHand: 3000 },
  { tail: 'n10', name: 'Lemons', kind: 'ingredient', baseUnit: 'each', par: 20, onHand: 40, unit: ['lemon', 'lemons'] },
  { tail: 'n11', name: 'Soda 300 ml', kind: 'ingredient', baseUnit: 'each', packs: [{ tail: 'n11', name: 'Case of 24', quantity: 24 }], par: 24, onHand: 48, unit: ['bottle', 'bottles'] },
  { tail: 'n12', name: 'Paper cups 250 ml', kind: 'supply', baseUnit: 'each', packs: [{ tail: 'n12', name: 'Sleeve of 50', quantity: 50 }], par: 100, onHand: 300, unit: ['cup', 'cups'] },
  { tail: 'n13', name: 'Cola can 330 ml', kind: 'ingredient', baseUnit: 'each', packs: [{ tail: 'n13', name: 'Case of 24', quantity: 24 }], par: 24, onHand: 60, unit: ['can', 'cans'] },
  { tail: 'n14', name: 'Spare mouse', kind: 'supply', baseUnit: 'each', par: 2, onHand: 4, unit: ['mouse', 'mice'] },
  // Tea counted in bags there, in grams here: the same name in another unit, so neither is copied over the other.
  { tail: 'n15', name: 'Tea leaves', kind: 'ingredient', baseUnit: 'each', par: 50, onHand: 120, unit: ['bag', 'bags'] },
  // A retail item: each venue links its own to its retail products, so it is never copied.
  { tail: 'n16', name: 'Energy drink 250 ml', kind: 'retail', baseUnit: 'each', par: 12, onHand: 20, productTail: 'r01', unit: ['can', 'cans'] },
];

function itemFrom(seed: Seed, createdAt: string): ItemRecord {
  const value = seed.cost === undefined ? 0 : round(BigInt(seed.onHand) * BigInt(seed.cost.paise), BigInt(seed.cost.per));
  return {
    id: id('ski', seed.tail),
    name: seed.name,
    kind: seed.kind,
    baseUnit: seed.baseUnit,
    sku: seed.sku ?? null,
    barcode: seed.barcode ?? null,
    productId: seed.productTail === undefined ? null : productId(seed.productTail),
    parLevel: seed.par,
    note: seed.note ?? null,
    unitName: seed.unit?.[0] ?? null,
    unitNamePlural: seed.unit?.[1] ?? null,
    supplierId: seed.supplier === undefined ? null : id('sup', seed.supplier),
    packs: (seed.packs ?? []).map((pack) => ({ id: id('skp', pack.tail), name: pack.name, quantity: pack.quantity, barcode: pack.barcode ?? null, archived: false })),
    balance: {
      quantity: seed.onHand,
      value: seed.onHand === 0 ? 0 : value,
      basis: seed.cost === undefined ? null : { value: seed.cost.paise, quantity: seed.cost.per },
    },
    lastMovementAt: null,
    lastCountedAt: seed.archived === true ? null : at('2026-09-15', '23:00'),
    archived: seed.archived ?? false,
    createdAt: at('2026-09-01', '10:00'),
    updatedAt: createdAt,
  };
}

interface RecipeRecord {
  readonly id: string;
  readonly productId: string;
  readonly variantId: string | null;
  readonly modifierId: string | null;
  components: { stockItemId: string; quantity: number }[];
}

const recipe = (tail: string, product: string, target: { variant?: string; modifier?: string }, components: readonly [string, number][]): RecipeRecord => ({
  id: id('rcp', tail),
  productId: productId(product),
  variantId: target.variant === undefined ? null : id('pvr', target.variant),
  modifierId: target.modifier === undefined ? null : id('pmo', target.modifier),
  components: components.map(([item, quantity]) => ({ stockItemId: id('ski', item), quantity })),
});

function seedRecipes(): RecipeRecord[] {
  return [
    recipe('a01', 'p01', {}, [['k01', 150], ['k02', 5]]),
    recipe('a02', 'p02', {}, [['k15', 6]]),
    recipe('a03', 'p03', {}, [['k12', 2], ['k11', 80], ['k03', 1]]),
    recipe('a04', 'p05', {}, [['k10', 150], ['k09', 120]]),
    recipe('a05', 'p05', { modifier: 'm01' }, [['k04', 80]]),
    recipe('a06', 'p05', { modifier: 'm02' }, [['k13', 1]]),
    recipe('a07', 'p09', { variant: 'v01' }, [['k05', 200], ['k06', 15], ['k08', 15]]),
    recipe('a08', 'p09', { variant: 'v02' }, [['k05', 300], ['k06', 22], ['k08', 20]]),
    recipe('a09', 'p10', {}, [['k05', 120], ['k07', 6], ['k08', 10]]),
    recipe('a10', 'p12', {}, [['k14', 1]]),
    recipe('a11', 'p13', {}, [['k16', 1]]),
  ];
}

/** Playground North's recipes, keyed on its items: two for dishes this venue has none for. */
function sourceRecipes(): RecipeRecord[] {
  return [
    recipe('b01', 'p01', {}, [['n01', 160], ['n02', 5]]),
    recipe('b02', 'p06', {}, [['n09', 180]]),
    recipe('b03', 'p11', {}, [['n10', 1], ['n11', 1], ['n06', 15]]),
    recipe('b04', 'p10', {}, [['n04', 120], ['n06', 10]]),
  ];
}

interface SupplierRecord {
  readonly id: string;
  name: string;
  phone: string | null;
  email: string | null;
  note: string | null;
  archived: boolean;
  readonly createdAt: string;
}

function seedSuppliers(): SupplierRecord[] {
  const supplier = (tail: string, name: string, phone: string | null, email: string | null, note: string | null = null, archived = false): SupplierRecord => ({
    id: id('sup', tail),
    name,
    phone,
    email,
    note,
    archived,
    createdAt: at('2026-09-01', '10:00'),
  });
  return [
    supplier('s01', 'Metro Cash & Carry', '+91 80 4123 9000', 'orders@metro.example', 'Delivers Tuesday and Friday mornings.'),
    supplier('s02', 'Fresh Farms Dairy', '+91 98450 22113', null, 'Milk and curd, daily before 9.'),
    supplier('s03', 'Coastline Beverages', '+91 80 2558 4410', 'sales@coastline.example'),
    supplier('s04', 'TechKart Supplies', null, 'hello@techkart.example', 'Keyboards, mice and cables.'),
    supplier('s05', 'Old Town Bakery', '+91 99001 23456', null, null, true),
  ];
}

interface MovementRecord extends Omit<StockMovement, 'value'> {
  readonly venueId: string;
  readonly value: number;
}

interface CountRecord {
  readonly id: string;
  readonly kind: StockCount['kind'];
  status: StockCount['status'];
  readonly note: string | null;
  readonly startedAt: string;
  readonly startedByName: string | null;
  submittedAt: string | null;
  submittedByName: string | null;
  cancelledAt: string | null;
  lines: CountLineRecord[];
  /** A submitted count's net variance, kept as it was. */
  varianceValue: number | null;
  submitNote: string | null;
}

/**
 * One line of a count: what was counted, as it was entered (packs and loose units) and when, and `expected` — on hand
 * **when the line was counted**, snapshot then, so a sale while the count is open does not move a counted shelf's
 * variance.
 */
interface CountLineRecord {
  stockItemId: string;
  counted: number | null;
  expected: number | null;
  variance: number | null;
  varianceValue: number | null;
  entered: { quantity: number; packs: { packId: string; name: string; quantity: number; count: number }[] } | null;
  countedAt: string | null;
}

interface ReceiptRecord extends Omit<StockReceipt, 'totalCost' | 'lines'> {
  readonly lines: (Omit<StockReceipt['lines'][number], 'cost'> & { readonly cost: number })[];
}

export interface InventoryOptions {
  /**
   * `off` (the default, as `inventory.enabled` is): status says so, nothing is listed, every write is `409`. `on`: the
   * seeded venue above. `new`: on, just turned on — the retail items made for the retail products, nothing counted.
   * `counting`: on, with a full count open and all but two of its items counted.
   */
  readonly mode?: 'off' | 'on' | 'new' | 'counting';
  /** `fnb.autoUnavailableOnZeroStock` at the venue; true by default. */
  readonly autoUnavailable?: boolean;
}

export interface InventoryHelpers {
  readonly json: (status: number, body: unknown, headers?: Record<string, string>) => FakeResponse;
  readonly problem: (status: number, code: string, title: string, extra?: Partial<Problem>) => FakeResponse;
  readonly can: (permission: string) => boolean;
}

const ROUTE = /^\/v1\/organizations\/([^/]+)\/venues\/([^/]+)\/inventory(\/.*)?$/;

export function createFakeInventory(helpers: InventoryHelpers, options: InventoryOptions = {}) {
  const { json, problem, can } = helpers;
  const mode = options.mode ?? 'off';
  let serial = 0;
  const fresh = (prefix: string) => {
    serial += 1;
    return numberedId(prefix, serial);
  };

  const state = {
    enabled: mode !== 'off',
    autoUnavailable: options.autoUnavailable ?? true,
    lastFullCountAt: mode === 'on' || mode === 'counting' ? at('2026-09-15', '23:00') : (null as string | null),
    items: new Map<string, ItemRecord[]>(),
    recipes: new Map<string, RecipeRecord[]>(),
    suppliers: seedSuppliers(),
    movements: [] as MovementRecord[],
    receipts: [] as ReceiptRecord[],
    counts: [] as CountRecord[],
    /** Idempotency keys already answered, with the body they answered. */
    keys: new Map<string, { body: string; answer: FakeResponse }>(),
    /** What the copy wrote, for tests to read back. */
    copies: [] as { sourceVenueId: string }[],
    /**
     * A test sets a path's tail (`/inventory/receipts`) to lose the answer to the next POST there: the write is made and
     * its key kept, but the caller hears a 503, as when the network drops the answer. A retry with the same key replays.
     */
    loseNextAnswerTo: null as string | null,
  };

  // -- seeding -----------------------------------------------------------------------------------

  if (mode !== 'off') {
    const seeds = mode === 'new' ? SEEDS.filter((seed) => seed.kind === 'retail').map((seed) => ({ ...seed, onHand: 0, cost: undefined, par: null })) : SEEDS;
    state.items.set(
      VENUE_ID,
      seeds.map((seed) => itemFrom(seed, at('2026-09-26', '18:00'))),
    );
    state.recipes.set(VENUE_ID, mode === 'new' ? [] : seedRecipes());
    if (mode !== 'new') seedHistory();
  }
  state.items.set(
    SECOND_VENUE_ID,
    SOURCE_SEEDS.map((seed) => itemFrom(seed, at('2026-09-20', '10:00'))),
  );
  state.recipes.set(SECOND_VENUE_ID, sourceRecipes());
  if (mode === 'counting') seedOpenCount();

  /** Every item's history, oldest first, so its movements sum to what it has on hand. */
  function seedHistory(): void {
    const items = state.items.get(VENUE_ID) ?? [];
    const metro = state.suppliers[0];
    const dairy = state.suppliers[1];
    const drinks = state.suppliers[2];
    // Three deliveries the Deliveries tab lists, newest first.
    const receipt = (tail: string, supplier: SupplierRecord | undefined, reference: string, receivedAt: string, by: string, lines: readonly [string, number, number, number?][]): ReceiptRecord => {
      const built = lines.map(([itemTail, quantity, cost, packCount]) => {
        const item = items.find((one) => one.id === id('ski', itemTail));
        const pack = item?.packs[0];
        return {
          stockItemId: id('ski', itemTail),
          name: item?.name ?? itemTail,
          baseUnit: item?.baseUnit ?? 'each',
          packs: pack !== undefined && packCount !== undefined ? [{ packId: pack.id, name: pack.name, quantity: pack.quantity, count: packCount }] : [],
          looseQuantity: pack !== undefined && packCount !== undefined ? quantity - pack.quantity * packCount : quantity,
          quantity,
          cost,
        };
      });
      return {
        id: id('skr', tail),
        supplierId: supplier?.id ?? null,
        supplierName: supplier?.name ?? null,
        reference,
        note: null,
        receivedAt,
        recordedByName: by,
        lines: built,
        createdAt: receivedAt,
      };
    };
    state.receipts.push(
      receipt('r03', drinks, 'CB/2209', at('2026-09-25', '10:15'), 'Kabir Mehta', [['k14', 48, 134_400, 2], ['k20', 24, 156_000, 1]]),
      receipt('r02', dairy, 'FF-8812', at('2026-09-25', '08:40'), 'Kabir Mehta', [['k05', 12_000, 67_200, 12], ['k04', 3000, 24_000, 3]]),
      receipt('r01', metro, 'INV-20419', at('2026-09-24', '11:00'), RECORDER, [['k01', 10_000, 72_000, 4], ['k10', 10_000, 110_000, 2], ['k08', 5000, 22_500, 1]]),
    );

    // Frozen fries: a lived-in history — deliveries, sales, a refund, waste, a staff meal, a count and a correction.
    const fries = items.find((one) => one.id === id('ski', 'k01'));
    if (fries !== undefined) {
      const history: [StockMovementKind, number, string, Partial<MovementRecord>][] = [
        ['receive', 7500, at('2026-09-10', '11:00'), { recordedByName: RECORDER }],
        ['sale', -300, at('2026-09-12', '20:10'), { saleId: id('sal', '9a1'), productId: productId('p01') }],
        ['sale', -450, at('2026-09-13', '19:40'), { saleId: id('sal', '9a2'), productId: productId('p01') }],
        ['waste', -500, at('2026-09-14', '13:05'), { reason: 'damaged', note: 'Bag split in the freezer.', recordedByName: 'Kabir Mehta' }],
        ['count', -250, at('2026-09-15', '23:00'), { countId: id('skc', 'c01'), recordedByName: RECORDER }],
        ['sale', -1500, at('2026-09-18', '21:15'), { saleId: id('sal', '9a3'), productId: productId('p01') }],
        ['staff_meal', -300, at('2026-09-20', '14:20'), { recordedByName: 'Kabir Mehta' }],
        ['sale', -2400, at('2026-09-22', '18:30'), { saleId: id('sal', '9a4'), productId: productId('p01') }],
        ['refund', 150, at('2026-09-23', '20:00'), { saleId: id('sal', '9a4'), refundId: id('srf', '9b1'), productId: productId('p01') }],
        ['receive', 10_000, at('2026-09-24', '11:00'), { receiptId: id('skr', 'r01'), recordedByName: RECORDER }],
        ['sale', -5550, at('2026-09-25', '21:40'), { saleId: id('sal', '9a5'), productId: productId('p01') }],
        ['sale', -3000, at('2026-09-26', '19:10'), { saleId: id('sal', '9a6'), productId: productId('p01') }],
        ['adjust', -200, at('2026-09-26', '22:00'), { note: 'Dropped a basket during service.', recordedByName: RECORDER }],
      ];
      seedMovements(fries, history);
    }
    // Every other item: a delivery (or a count) in, and what was sold or used since — ending at what it has on hand.
    for (const item of items) {
      if (item.id === fries?.id || item.archived) continue;
      const sold = item.kind === 'supply' ? Math.max(1, Math.round(Math.abs(item.balance.quantity) / 3)) : Math.max(1, Math.round(Math.abs(item.balance.quantity) / 2));
      const received = item.balance.quantity + sold;
      const inKind: StockMovementKind = item.balance.basis === null ? 'count' : 'receive';
      const outKind: StockMovementKind = item.kind === 'supply' ? 'issue' : 'sale';
      seedMovements(item, [
        [inKind, received, at('2026-09-24', '11:00'), { recordedByName: RECORDER, ...(inKind === 'count' ? { countId: id('skc', 'c01') } : {}) }],
        [outKind, -sold, at('2026-09-26', '18:45'), outKind === 'issue' ? { recordedByName: 'Kabir Mehta' } : { saleId: id('sal', '9a7'), ...(item.productId === null ? {} : { productId: item.productId }) }],
      ]);
    }

    // Two counts already submitted, for the Counts tab and the variance report.
    state.counts.push(
      {
        id: id('skc', 'c02'),
        kind: 'spot',
        status: 'submitted',
        note: 'Fridge check after the power cut.',
        startedAt: at('2026-09-21', '10:05'),
        startedByName: 'Kabir Mehta',
        submittedAt: at('2026-09-21', '10:20'),
        submittedByName: 'Kabir Mehta',
        cancelledAt: null,
        lines: [
          { stockItemId: id('ski', 'k05'), counted: 6000, expected: 6800, variance: -800, varianceValue: -4480, entered: { quantity: 0, packs: [{ packId: id('skp', 'k05'), name: '1 L packet', quantity: 1000, count: 6 }] }, countedAt: at('2026-09-21', '10:10') },
          { stockItemId: id('ski', 'k04'), counted: 1200, expected: 1500, variance: -300, varianceValue: -2400, entered: { quantity: 1200, packs: [] }, countedAt: at('2026-09-21', '10:12') },
          { stockItemId: id('ski', 'k11'), counted: 400, expected: 800, variance: -400, varianceValue: -14_000, entered: { quantity: 400, packs: [] }, countedAt: at('2026-09-21', '10:15') },
        ],
        varianceValue: -20_880,
        submitNote: 'The fridge was off overnight.',
      },
      {
        id: id('skc', 'c01'),
        kind: 'full',
        status: 'submitted',
        note: 'First full count.',
        startedAt: at('2026-09-15', '21:40'),
        startedByName: RECORDER,
        submittedAt: at('2026-09-15', '23:00'),
        submittedByName: RECORDER,
        cancelledAt: null,
        lines: items
          .filter((item) => !item.archived)
          .map((item, index) => {
            const variance = [-250, 0, -4, 0, -500, 0, 20, 0, -300, 0, -200, 2, 0, -1, 0, -1][index] ?? 0;
            const cost = unitCostOf(item.balance);
            const value = cost === null ? null : round(BigInt(variance) * BigInt(cost.amount.amount), BigInt(cost.quantity));
            return { stockItemId: item.id, counted: 1000 + variance, expected: 1000, variance, varianceValue: value, entered: { quantity: 1000 + variance, packs: [] }, countedAt: at('2026-09-15', '22:30') };
          }),
        varianceValue: 0,
        submitNote: null,
      },
    );
    const full = state.counts[1];
    if (full !== undefined) full.varianceValue = full.lines.reduce((sum, line) => sum + (line.varianceValue ?? 0), 0);
  }

  /** Write a seeded history backwards from the item's balance, so the last movement leaves exactly what it has. */
  function seedMovements(item: ItemRecord, history: readonly [StockMovementKind, number, string, Partial<MovementRecord>][]): void {
    const total = history.reduce((sum, [, change]) => sum + change, 0);
    let after = item.balance.quantity - total;
    const cost = unitCostOf(item.balance);
    for (const [kind, change, occurredAt, extra] of history) {
      after += change;
      state.movements.push({
        id: fresh('skm'),
        venueId: VENUE_ID,
        stockItemId: item.id,
        kind,
        quantity: change,
        quantityAfter: after,
        value: cost === null ? 0 : round(BigInt(change) * BigInt(cost.amount.amount), BigInt(cost.quantity)),
        reason: null,
        note: null,
        saleId: null,
        refundId: null,
        receiptId: null,
        countId: null,
        productId: null,
        stationId: null,
        recordedByName: null,
        occurredAt,
        ...extra,
      });
      item.lastMovementAt = occurredAt;
    }
  }

  /** A full count, open, its items counted but two — the variance review a manager posts. */
  function seedOpenCount(): void {
    const items = (state.items.get(VENUE_ID) ?? []).filter((item) => !item.archived);
    const finds: Readonly<Record<string, number>> = { k01: 2950, k03: 30, k05: 8000, k06: 2350, k09: 0, k11: 0, k14: 38, k16: 9, k30: 3400 };
    state.counts.unshift({
      id: id('skc', 'c03'),
      kind: 'full',
      status: 'open',
      note: 'Month-end count.',
      startedAt: at('2026-09-26', '22:15'),
      startedByName: RECORDER,
      submittedAt: null,
      submittedByName: null,
      cancelledAt: null,
      lines: items.map((item, index) => {
        const tail = item.id.slice(-3);
        const counted = index >= items.length - 2 ? null : (finds[tail] ?? Math.max(0, item.balance.quantity));
        if (counted === null) return { stockItemId: item.id, counted, expected: null, variance: null, varianceValue: null, entered: null, countedAt: null };
        // The fries were counted a bag and a loose 450 g, and are shown back that way.
        const pack = tail === 'k01' ? item.packs[0] : undefined;
        const entered = pack === undefined ? { quantity: counted, packs: [] } : { quantity: counted - pack.quantity, packs: [{ packId: pack.id, name: pack.name, quantity: pack.quantity, count: 1 }] };
        return { stockItemId: item.id, counted, expected: item.balance.quantity, variance: null, varianceValue: null, entered, countedAt: at('2026-09-26', '22:40') };
      }),
      varianceValue: null,
      submitNote: null,
    });
  }

  // -- reading -------------------------------------------------------------------------------------

  const itemsAt = (venueId: string) => state.items.get(venueId) ?? [];
  const recipesAt = (venueId: string) => state.recipes.get(venueId) ?? [];
  const findItem = (venueId: string, stockItemId: string) => itemsAt(venueId).find((item) => item.id === stockItemId);
  const reports = () => can('inventory:reports');
  const money = (amount: number | null): Money | null => (amount === null || !reports() ? null : inr(amount));

  function levelOf(item: ItemRecord): StockLevel {
    if (item.balance.quantity < 0) return 'negative';
    if (item.balance.quantity === 0) return 'out';
    if (item.parLevel !== null && item.balance.quantity < item.parLevel) return 'low';
    return 'ok';
  }

  function itemDto(item: ItemRecord): StockItem {
    return {
      id: item.id,
      name: item.name,
      kind: item.kind,
      baseUnit: item.baseUnit,
      sku: item.sku,
      barcode: item.barcode,
      productId: item.productId,
      parLevel: item.parLevel,
      note: item.note,
      unitName: item.unitName,
      unitNamePlural: item.unitNamePlural,
      supplierId: item.supplierId,
      packs: item.packs.filter((pack) => !pack.archived),
      onHand: item.balance.quantity,
      level: levelOf(item),
      averageUnitCost: reports() ? unitCostOf(item.balance) : null,
      value: money(item.balance.value),
      lastMovementAt: item.lastMovementAt,
      lastCountedAt: item.lastCountedAt,
      archived: item.archived,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
    };
  }

  function movementDto(movement: MovementRecord): StockMovement {
    return { ...movement, value: money(movement.value) };
  }

  /** A recipe's cost: Σ quantity × value ÷ quantity, summed exactly as a fraction, rounded once (ADR-0040 §15). */
  function costOf(venueId: string, components: readonly { stockItemId: string; quantity: number }[]): { cost: number; complete: boolean } {
    let numerator = 0n;
    let denominator = 1n;
    let complete = true;
    for (const component of components) {
      const item = findItem(venueId, component.stockItemId);
      const unit = item === undefined ? null : unitCostOf(item.balance);
      if (unit === null) {
        complete = false;
        continue;
      }
      const q = BigInt(unit.quantity);
      numerator = numerator * q + BigInt(component.quantity) * BigInt(unit.amount.amount) * denominator;
      denominator *= q;
    }
    return { cost: round(numerator, denominator), complete };
  }

  const makeable = (venueId: string, components: readonly { stockItemId: string; quantity: number }[]) =>
    components.every((component) => (findItem(venueId, component.stockItemId)?.balance.quantity ?? 0) >= component.quantity);

  function recipeName(record: RecipeRecord): string {
    const product = COMMERCE_PRODUCTS.find((one) => one.id === record.productId);
    const name = product?.name ?? 'Product';
    if (record.variantId !== null) return `${name} - ${product?.variants.find((variant) => variant.id === record.variantId)?.name ?? 'Size'}`;
    if (record.modifierId !== null) {
      return product?.modifierGroups.flatMap((group) => group.modifiers).find((modifier) => modifier.id === record.modifierId)?.name ?? 'Extra';
    }
    return name;
  }

  /** What a recipe's target sells for here: the product's, the size's or the extra's own price, and before tax. */
  function priceOf(record: Pick<RecipeRecord, 'productId' | 'variantId' | 'modifierId'>): { price: Money; priceExclTax: Money } {
    const product = COMMERCE_PRODUCTS.find((one) => one.id === record.productId);
    const price =
      record.modifierId !== null
        ? (product?.modifierGroups.flatMap((group) => group.modifiers).find((modifier) => modifier.id === record.modifierId)?.price ?? inr(0))
        : record.variantId !== null
          ? (product?.variants.find((variant) => variant.id === record.variantId)?.price ?? inr(0))
          : (product?.price ?? inr(0));
    const priced = priceFoodOrderLine({ unitPrice: price, modifierPrices: [], quantity: 1, taxRateBps: product?.taxRateBps ?? 0 }, TREATMENT);
    return { price, priceExclTax: priced.taxable };
  }

  /** The margin on a price before tax, and as basis points of it — the ₹ figures need `inventory:reports`. */
  function marginOf(priceExclTax: Money, cost: number): { margin: Money | null; marginBps: number | null } {
    const margin = priceExclTax.amount - cost;
    return {
      margin: money(margin),
      marginBps: !reports() || priceExclTax.amount === 0 ? null : round(BigInt(margin) * 10_000n, BigInt(priceExclTax.amount)),
    };
  }

  function recipeDto(venueId: string, record: RecipeRecord): StockRecipe {
    const { cost, complete } = costOf(venueId, record.components);
    const prices = priceOf(record);
    return {
      id: record.id,
      productId: record.productId,
      variantId: record.variantId,
      modifierId: record.modifierId,
      name: recipeName(record),
      components: record.components.map((component) => {
        const item = findItem(venueId, component.stockItemId);
        const unit = item === undefined ? null : unitCostOf(item.balance);
        return {
          stockItemId: component.stockItemId,
          name: item?.name ?? 'Item',
          baseUnit: item?.baseUnit ?? 'each',
          quantity: component.quantity,
          cost: unit === null ? null : money(round(BigInt(component.quantity) * BigInt(unit.amount.amount), BigInt(unit.quantity))),
        };
      }),
      cost: money(cost),
      costComplete: complete,
      ...prices,
      ...marginOf(prices.priceExclTax, cost),
      makeable: makeable(venueId, record.components),
    };
  }

  /** Each thing a player buys of a product — the product, or each size — with the recipe it takes and what it earns. */
  function unitsOf(venueId: string, productIdValue: string) {
    const product = COMMERCE_PRODUCTS.find((one) => one.id === productIdValue);
    const own = recipesAt(venueId).filter((record) => record.productId === productIdValue && record.modifierId === null);
    const base = own.find((record) => record.variantId === null);
    const retail = itemsAt(venueId).find((item) => item.productId === productIdValue);
    const targets = product === undefined || product.variants.length === 0 ? [{ variantId: null as string | null, name: product?.name ?? retail?.name ?? 'Product' }] : product.variants.map((variant) => ({ variantId: variant.id as string | null, name: `${product.name} - ${variant.name}` }));
    return targets.map((target) => {
      const recipe = (target.variantId === null ? undefined : own.find((record) => record.variantId === target.variantId)) ?? base;
      const prices = priceOf({ productId: productIdValue, variantId: target.variantId, modifierId: null });
      const components = recipe?.components ?? (retail === undefined ? [] : [{ stockItemId: retail.id, quantity: 1 }]);
      const tracked = components.length > 0;
      const { cost, complete } = costOf(venueId, components);
      return {
        variantId: target.variantId,
        name: target.name,
        recipeId: recipe?.id ?? null,
        tracked,
        ...prices,
        cost: tracked ? money(cost) : null,
        costComplete: complete,
        ...(tracked ? marginOf(prices.priceExclTax, cost) : { margin: null, marginBps: null }),
        makeable: tracked && makeable(venueId, components),
      };
    });
  }

  /** A product none of whose recipes (its base, or each variant's effective one) can be made: sold out by stock. */
  function soldOutForStock(venueId: string, productIdValue: string): boolean {
    if (!state.enabled || !state.autoUnavailable) return false;
    const product = COMMERCE_PRODUCTS.find((one) => one.id === productIdValue);
    const own = recipesAt(venueId).filter((record) => record.productId === productIdValue && record.modifierId === null);
    if (own.length === 0) return false;
    const base = own.find((record) => record.variantId === null);
    const effective = product === undefined || product.variants.length === 0 ? [base] : product.variants.map((variant) => own.find((record) => record.variantId === variant.id) ?? base);
    const known = effective.filter((record): record is RecipeRecord => record !== undefined);
    return known.length > 0 && known.every((record) => !makeable(venueId, record.components));
  }

  function soldOut(venueId: string) {
    const products = [...new Set(recipesAt(venueId).map((record) => record.productId))];
    return products.filter((one) => soldOutForStock(venueId, one)).map((one) => ({ productId: one, name: COMMERCE_PRODUCTS.find((product) => product.id === one)?.name ?? 'Product' }));
  }

  function countDto(venueId: string, record: CountRecord, withLines = true): StockCount {
    const lines: StockCountLine[] = record.lines.map((line) => {
      const item = findItem(venueId, line.stockItemId);
      const shared = { stockItemId: line.stockItemId, name: item?.name ?? 'Item', baseUnit: item?.baseUnit ?? 'each', counted: line.counted, entered: line.entered, countedAt: line.countedAt };
      if (record.status !== 'open') {
        return { ...shared, expected: line.expected ?? 0, variance: line.variance, varianceValue: money(line.varianceValue) };
      }
      // While open: a counted line's expected was snapshot when it was counted; an uncounted one's is on hand now.
      const expected = line.counted !== null && line.expected !== null ? line.expected : (item?.balance.quantity ?? 0);
      const variance = line.counted === null ? null : line.counted - expected;
      let value: number | null = null;
      if (variance !== null && item !== undefined && unitCostOf(item.balance) !== null) value = variance < 0 ? -valueOut(item.balance, -variance) : valueIn(item.balance, variance);
      return { ...shared, expected, variance, varianceValue: money(value) };
    });
    const total = record.status === 'open' ? lines.reduce((sum, line) => sum + (line.varianceValue?.amount ?? 0), 0) : record.varianceValue;
    const summary = {
      id: record.id,
      kind: record.kind,
      status: record.status,
      note: record.note,
      startedAt: record.startedAt,
      startedByName: record.startedByName,
      submittedAt: record.submittedAt,
      submittedByName: record.submittedByName,
      cancelledAt: record.cancelledAt,
      submitNote: record.submitNote,
      lineCount: record.lines.length,
      countedCount: record.lines.filter((line) => line.counted !== null).length,
      varianceValue: money(total),
    };
    return (withLines ? { ...summary, lines } : summary) as StockCount;
  }

  function receiptDto(record: ReceiptRecord): StockReceipt {
    return {
      ...record,
      lines: record.lines.map((line) => ({ ...line, cost: inr(line.cost) })),
      totalCost: inr(record.lines.reduce((sum, line) => sum + line.cost, 0)),
    };
  }

  function page<T>(rows: readonly T[], searchParams: URLSearchParams) {
    const limit = Math.min(100, Math.max(1, Number(searchParams.get('limit') ?? 25) || 25));
    const start = Number(searchParams.get('cursor') ?? 0) || 0;
    const data = rows.slice(start, start + limit);
    const next = start + limit < rows.length ? String(start + limit) : null;
    return { data, page: { cursor: next, hasMore: next !== null, count: data.length } };
  }

  // -- writing -------------------------------------------------------------------------------------

  /** Record one movement against an item, keeping its balance and its last movement. */
  function move(venueId: string, item: ItemRecord, kind: StockMovementKind, change: number, extra: Partial<MovementRecord> = {}, cost?: number): MovementRecord {
    let value: number;
    if (kind === 'receive') value = receive(item.balance, change, cost ?? 0);
    else {
      value = change < 0 ? -valueOut(item.balance, -change) : valueIn(item.balance, change);
      apply(item.balance, change, value);
    }
    const movement: MovementRecord = {
      id: fresh('skm'),
      venueId,
      stockItemId: item.id,
      kind,
      quantity: change,
      quantityAfter: item.balance.quantity,
      value,
      reason: null,
      note: null,
      saleId: null,
      refundId: null,
      receiptId: null,
      countId: null,
      productId: null,
      stationId: null,
      recordedByName: RECORDER,
      occurredAt: INVENTORY_NOW,
      ...extra,
    };
    state.movements.push(movement);
    item.lastMovementAt = movement.occurredAt;
    item.updatedAt = movement.occurredAt;
    return movement;
  }

  /** Packs and loose units → base units, or a refusal naming the pack that is not the item's. */
  function quantityOf(item: ItemRecord, loose: number | undefined, packs: readonly { packId: string; count: number }[] | undefined): number | FakeResponse {
    let total = loose ?? 0;
    for (const entry of packs ?? []) {
      const pack = item.packs.find((one) => one.id === entry.packId);
      if (pack === undefined) return problem(404, 'not_found', 'No such pack for that item.');
      total += pack.quantity * entry.count;
    }
    return total;
  }

  const invalid = (error: z.ZodError) =>
    problem(400, 'validation_failed', 'Validation failed', {
      detail: 'The request could not be understood as written.',
      errors: error.issues.map((issue) => ({ field: issue.path.map(String).join('.') || '(request)', code: issue.code, message: issue.message })),
    });
  const forbidden = () => problem(403, 'forbidden', 'Not allowed');
  const off = () => problem(409, 'inventory_disabled', 'Stock tracking is off here', { detail: 'Turn on "Track stock" in the venue’s Food and drinks settings first.' });
  const notFound = (title: string) => problem(404, 'not_found', title);

  /**
   * The route's own permission, or — where the route declares `inventory:supplies` as its narrower one — that, for
   * supply items only. Answers whether the caller is held to supplies (`narrow`), or a 403.
   */
  function allow(permission: string, narrowable: boolean): { narrow: boolean } | FakeResponse {
    if (can(permission)) return { narrow: false };
    if (narrowable && can('inventory:supplies')) return { narrow: true };
    return forbidden();
  }

  /**
   * The stock-changing POSTs **require** an `Idempotency-Key` (400 without one); one already answered for the same body
   * answers the same again, and for another body 422.
   */
  function idempotent(key: string | undefined, body: unknown, run: () => FakeResponse): FakeResponse {
    if (key === undefined || key.trim() === '') return problem(400, 'validation_failed', 'An Idempotency-Key is required.', { errors: [{ field: 'idempotency-key', code: 'invalid_type', message: 'Required' }] });
    const text = JSON.stringify(body);
    const seen = state.keys.get(key);
    if (seen !== undefined) return seen.body === text ? seen.answer : problem(422, 'idempotency_key_reused', 'That key was used for another request.');
    const answer = run();
    if (answer.status < 300) state.keys.set(key, { body: text, answer });
    return answer;
  }

  // -- the routes ----------------------------------------------------------------------------------

  function handle(method: string, pathname: string, searchParams: URLSearchParams, input: Record<string, unknown>, headers: Readonly<Record<string, string>> = {}): FakeResponse | undefined {
    const answer = route(method, pathname, searchParams, input, headers);
    const lost = state.loseNextAnswerTo;
    if (answer === undefined || lost === null || method !== 'POST' || !pathname.endsWith(lost)) return answer;
    state.loseNextAnswerTo = null;
    return problem(503, 'service_unavailable', 'The answer was lost on the way back.');
  }

  function route(method: string, pathname: string, searchParams: URLSearchParams, input: Record<string, unknown>, headers: Readonly<Record<string, string>>): FakeResponse | undefined {
    const matched = ROUTE.exec(pathname);
    if (matched === null) return undefined;
    const [, organizationId, venueId = '', rest = ''] = matched;
    if (organizationId !== ORGANIZATION_ID || (venueId !== VENUE_ID && venueId !== SECOND_VENUE_ID)) return notFound('No such venue.');
    // The member's other venue keeps its own stock on, so its items can be copied; this venue follows the option.
    const enabledHere = venueId === SECOND_VENUE_ID || state.enabled;
    const key = headers['idempotency-key'];
    const read = () => (can('inventory:read') ? undefined : forbidden());

    if (rest === '/status' && method === 'GET') {
      const refused = read();
      if (refused !== undefined) return refused;
      const items = itemsAt(venueId).filter((item) => !item.archived);
      const levels = items.map(levelOf);
      return json(200, {
        enabled: enabledHere,
        autoUnavailableOnZeroStock: state.autoUnavailable,
        itemCount: enabledHere ? items.length : 0,
        lowCount: enabledHere ? levels.filter((level) => level === 'low').length : 0,
        outCount: enabledHere ? levels.filter((level) => level === 'out').length : 0,
        negativeCount: enabledHere ? levels.filter((level) => level === 'negative').length : 0,
        soldOutCount: enabledHere ? soldOut(venueId).length : 0,
        lastFullCountAt: venueId === VENUE_ID ? state.lastFullCountAt : at('2026-09-20', '22:00'),
      });
    }

    if (rest === '/alerts' && method === 'GET') {
      const refused = read();
      if (refused !== undefined) return refused;
      const alert = (item: ItemRecord) => ({ stockItemId: item.id, name: item.name, kind: item.kind, baseUnit: item.baseUnit, onHand: item.balance.quantity, parLevel: item.parLevel, level: levelOf(item) });
      const items = enabledHere ? itemsAt(venueId).filter((item) => !item.archived) : [];
      return json(200, {
        enabled: enabledHere,
        low: items.filter((item) => levelOf(item) === 'low').map(alert),
        out: items.filter((item) => levelOf(item) === 'out').map(alert),
        negative: items.filter((item) => levelOf(item) === 'negative').map(alert),
        soldOut: enabledHere ? soldOut(venueId) : [],
      });
    }

    if (rest === '/items') {
      if (method === 'GET') {
        const refused = read();
        if (refused !== undefined) return refused;
        const q = (searchParams.get('q') ?? '').toLowerCase();
        const kind = searchParams.get('kind');
        const level = searchParams.get('level');
        const archived = searchParams.get('includeArchived') === 'true';
        const items = (enabledHere || itemsAt(venueId).length > 0 ? itemsAt(venueId) : [])
          .filter((item) => archived || !item.archived)
          .filter((item) => kind === null || item.kind === kind)
          .filter((item) => level === null || levelOf(item) === level)
          .filter((item) => q === '' || [item.name, item.sku ?? '', item.barcode ?? '', ...item.packs.map((pack) => pack.barcode ?? '')].some((text) => text.toLowerCase().includes(q)))
          .sort((a, b) => a.name.localeCompare(b.name));
        return json(200, { items: venueId === VENUE_ID && !state.enabled ? [] : items.map(itemDto) });
      }
      if (method === 'POST') {
        const allowed = allow('inventory:manage', true);
        if ('status' in allowed) return allowed;
        if (!enabledHere) return off();
        const parsed = CreateStockItemSchema.safeParse(input);
        if (!parsed.success) return invalid(parsed.error);
        if (parsed.data.kind === 'retail') return problem(400, 'validation_failed', 'A retail item is made for each retail product.');
        if (parsed.data.baseUnit !== 'each' && ((parsed.data.unitName ?? null) !== null || (parsed.data.unitNamePlural ?? null) !== null)) return problem(400, 'validation_failed', 'Only an item counted in pieces has a unit name.');
        if (parsed.data.supplierId != null && !state.suppliers.some((one) => one.id === parsed.data.supplierId)) return notFound('No such supplier.');
        if (allowed.narrow && parsed.data.kind !== 'supply') return forbidden();
        const now = INVENTORY_NOW;
        const record: ItemRecord = {
          id: fresh('ski'),
          name: parsed.data.name,
          kind: parsed.data.kind,
          baseUnit: parsed.data.baseUnit,
          sku: parsed.data.sku ?? null,
          barcode: parsed.data.barcode ?? null,
          productId: null,
          parLevel: parsed.data.parLevel ?? null,
          note: parsed.data.note ?? null,
          unitName: parsed.data.unitName ?? null,
          unitNamePlural: parsed.data.unitNamePlural ?? null,
          supplierId: parsed.data.supplierId ?? null,
          packs: (parsed.data.packs ?? []).map((pack) => ({ id: fresh('skp'), name: pack.name, quantity: pack.quantity, barcode: pack.barcode ?? null, archived: false })),
          balance: { quantity: 0, value: 0, basis: null },
          lastMovementAt: null,
          lastCountedAt: null,
          archived: false,
          createdAt: now,
          updatedAt: now,
        };
        state.items.set(venueId, [...itemsAt(venueId), record]);
        return json(201, itemDto(record));
      }
      return undefined;
    }

    const itemPath = /^\/items\/([^/]+)$/.exec(rest);
    if (itemPath !== null) {
      const item = findItem(venueId, itemPath[1] ?? '');
      if (method === 'GET') {
        const refused = read();
        if (refused !== undefined) return refused;
        return item === undefined ? notFound('No such item at this venue.') : json(200, itemDto(item));
      }
      if (method === 'PATCH') {
        const allowed = allow('inventory:manage', true);
        if ('status' in allowed) return allowed;
        if (!enabledHere) return off();
        if (item === undefined) return notFound('No such item at this venue.');
        if (allowed.narrow && item.kind !== 'supply') return forbidden();
        const parsed = UpdateStockItemSchema.safeParse(input);
        if (!parsed.success) return invalid(parsed.error);
        const change = parsed.data;
        if (change.name !== undefined && item.kind === 'retail') return problem(400, 'validation_failed', 'A retail item is named after its product.');
        if (change.baseUnit !== undefined && change.baseUnit !== item.baseUnit && state.movements.some((movement) => movement.stockItemId === item.id)) {
          return problem(409, 'conflict', 'Conflict', { detail: 'The unit of an item that has moved cannot change.' });
        }
        if (change.name !== undefined) item.name = change.name;
        if (change.baseUnit !== undefined) item.baseUnit = change.baseUnit;
        if (change.sku !== undefined) item.sku = change.sku;
        if (change.barcode !== undefined) item.barcode = change.barcode;
        if (change.parLevel !== undefined) item.parLevel = change.parLevel;
        if (change.note !== undefined) item.note = change.note;
        if (change.unitName !== undefined) item.unitName = change.unitName;
        if (change.unitNamePlural !== undefined) item.unitNamePlural = change.unitNamePlural;
        if (change.supplierId !== undefined) {
          if (change.supplierId !== null && !state.suppliers.some((one) => one.id === change.supplierId)) return notFound('No such supplier.');
          item.supplierId = change.supplierId;
        }
        if (item.baseUnit !== 'each') {
          item.unitName = null;
          item.unitNamePlural = null;
        }
        if (change.archived !== undefined) item.archived = change.archived;
        if (change.packs !== undefined) {
          const kept = change.packs.map((pack) => {
            const existing = pack.id === undefined ? undefined : item.packs.find((one) => one.id === pack.id);
            return { id: existing?.id ?? fresh('skp'), name: pack.name, quantity: pack.quantity, barcode: pack.barcode ?? null, archived: false };
          });
          const dropped = item.packs.filter((pack) => !kept.some((one) => one.id === pack.id)).map((pack) => ({ ...pack, archived: true }));
          item.packs = [...kept, ...dropped];
        }
        item.updatedAt = INVENTORY_NOW;
        return json(200, itemDto(item));
      }
      return undefined;
    }

    if (rest === '/lookup' && method === 'GET') {
      const refused = read();
      if (refused !== undefined) return refused;
      const barcode = searchParams.get('barcode') ?? '';
      for (const item of itemsAt(venueId)) {
        if (item.archived) continue;
        const pack = item.packs.find((one) => !one.archived && one.barcode === barcode);
        if (pack !== undefined) return json(200, { item: itemDto(item), pack });
        if (item.barcode === barcode) return json(200, { item: itemDto(item), pack: null });
      }
      return notFound('No stock item at this venue has that barcode.');
    }

    if (rest === '/movements' && method === 'GET') {
      const refused = read();
      if (refused !== undefined) return refused;
      const itemId = searchParams.get('stockItemId');
      const kind = searchParams.get('kind');
      // Business days (06:00 in Asia/Kolkata): a day starts at 00:30 UTC; with an instant too, the narrower bound applies.
      const fromDay = searchParams.get('fromDay');
      const toDay = searchParams.get('toDay');
      const dayStart = (day: string) => `${day}T00:30:00.000Z`;
      const dayEnd = (day: string) => {
        const end = new Date(dayStart(day));
        end.setUTCDate(end.getUTCDate() + 1);
        return end.toISOString();
      };
      const later = (a: string | null, b: string | null) => (a === null ? b : b === null ? a : a > b ? a : b);
      const earlier = (a: string | null, b: string | null) => (a === null ? b : b === null ? a : a < b ? a : b);
      const from = later(searchParams.get('from'), fromDay === null ? null : dayStart(fromDay));
      const to = earlier(searchParams.get('to'), toDay === null ? null : dayEnd(toDay));
      if (itemId !== null && findItem(venueId, itemId) === undefined) return notFound('No such item at this venue.');
      const rows = state.movements
        .filter((movement) => movement.venueId === venueId)
        .filter((movement) => itemId === null || movement.stockItemId === itemId)
        .filter((movement) => kind === null || movement.kind === kind)
        .filter((movement) => from === null || movement.occurredAt >= from)
        .filter((movement) => to === null || movement.occurredAt < to)
        .map((movement, index) => ({ movement, index }))
        .sort((a, b) => b.movement.occurredAt.localeCompare(a.movement.occurredAt) || b.index - a.index)
        .map(({ movement }) => movementDto(movement));
      return json(200, page(rows, searchParams));
    }

    if (rest === '/suppliers') {
      if (method === 'GET') {
        const refused = read();
        if (refused !== undefined) return refused;
        const archived = searchParams.get('includeArchived') === 'true';
        return json(200, { suppliers: state.suppliers.filter((one) => archived || !one.archived).sort((a, b) => a.name.localeCompare(b.name)) });
      }
      if (method === 'POST') {
        if (!can('inventory:manage')) return forbidden();
        if (!enabledHere) return off();
        const parsed = CreateStockSupplierSchema.safeParse(input);
        if (!parsed.success) return invalid(parsed.error);
        const record: SupplierRecord = { id: fresh('sup'), name: parsed.data.name, phone: parsed.data.phone ?? null, email: parsed.data.email ?? null, note: parsed.data.note ?? null, archived: false, createdAt: INVENTORY_NOW };
        state.suppliers.push(record);
        return json(201, record satisfies StockSupplier);
      }
      return undefined;
    }

    const supplierPath = /^\/suppliers\/([^/]+)$/.exec(rest);
    if (supplierPath !== null && method === 'PATCH') {
      if (!can('inventory:manage')) return forbidden();
      if (!enabledHere) return off();
      const record = state.suppliers.find((one) => one.id === supplierPath[1]);
      if (record === undefined) return notFound('No such supplier at this venue.');
      const parsed = UpdateStockSupplierSchema.safeParse(input);
      if (!parsed.success) return invalid(parsed.error);
      if (parsed.data.name !== undefined) record.name = parsed.data.name;
      if (parsed.data.phone !== undefined) record.phone = parsed.data.phone;
      if (parsed.data.email !== undefined) record.email = parsed.data.email;
      if (parsed.data.note !== undefined) record.note = parsed.data.note;
      if (parsed.data.archived !== undefined) record.archived = parsed.data.archived;
      return json(200, record satisfies StockSupplier);
    }

    if (rest === '/receipts') {
      if (method === 'GET') {
        const refused = read();
        if (refused !== undefined) return refused;
        const rows = [...state.receipts].sort((a, b) => b.receivedAt.localeCompare(a.receivedAt)).map(receiptDto);
        return json(200, page(rows, searchParams));
      }
      if (method === 'POST') {
        const allowed = allow('inventory:receive', true);
        if ('status' in allowed) return allowed;
        if (!enabledHere) return off();
        const parsed = CreateStockReceiptSchema.safeParse(input);
        if (!parsed.success) return invalid(parsed.error);
        return idempotent(key, input, () => {
          const data = parsed.data;
          const supplier = data.supplierId === undefined ? undefined : state.suppliers.find((one) => one.id === data.supplierId);
          if (data.supplierId !== undefined && supplier === undefined) return notFound('No such supplier.');
          if (data.receivedAt !== undefined && data.receivedAt > INVENTORY_NOW) return problem(400, 'validation_failed', 'A delivery cannot arrive in the future.');
          const planned: { item: ItemRecord; quantity: number; cost: number; packs: { packId: string; name: string; quantity: number; count: number }[]; loose: number }[] = [];
          for (const line of data.lines) {
            const item = findItem(venueId, line.stockItemId);
            if (item === undefined || item.archived) return notFound('No such item at this venue.');
            if (allowed.narrow && item.kind !== 'supply') return forbidden();
            const quantity = quantityOf(item, line.quantity, line.packs);
            if (typeof quantity !== 'number') return quantity;
            if (quantity <= 0) return problem(400, 'validation_failed', 'Each line receives something.');
            planned.push({
              item,
              quantity,
              cost: line.cost.amount,
              loose: line.quantity ?? 0,
              packs: (line.packs ?? []).map((entry) => {
                const pack = item.packs.find((one) => one.id === entry.packId);
                return { packId: entry.packId, name: pack?.name ?? 'Pack', quantity: pack?.quantity ?? 1, count: entry.count };
              }),
            });
          }
          const receiptId = fresh('skr');
          const receivedAt = data.receivedAt ?? INVENTORY_NOW;
          for (const line of planned) move(venueId, line.item, 'receive', line.quantity, { receiptId, occurredAt: receivedAt }, line.cost);
          const record: ReceiptRecord = {
            id: receiptId,
            supplierId: supplier?.id ?? null,
            supplierName: supplier?.name ?? null,
            reference: data.reference ?? null,
            note: data.note ?? null,
            receivedAt,
            recordedByName: RECORDER,
            lines: planned.map((line) => ({ stockItemId: line.item.id, name: line.item.name, baseUnit: line.item.baseUnit, packs: line.packs, looseQuantity: line.loose, quantity: line.quantity, cost: line.cost })),
            createdAt: INVENTORY_NOW,
          };
          state.receipts.push(record);
          return json(201, receiptDto(record));
        });
      }
      return undefined;
    }

    const receiptPath = /^\/receipts\/([^/]+)$/.exec(rest);
    if (receiptPath !== null && method === 'GET') {
      const refused = read();
      if (refused !== undefined) return refused;
      const record = state.receipts.find((one) => one.id === receiptPath[1]);
      return record === undefined ? notFound('No such receipt at this venue.') : json(200, receiptDto(record));
    }

    if (rest === '/usage' && method === 'POST') {
      const allowed = allow('inventory:waste', true);
      if ('status' in allowed) return allowed;
      if (!enabledHere) return off();
      const parsed = CreateStockUsageSchema.safeParse(input);
      if (!parsed.success) return invalid(parsed.error);
      const data = parsed.data;
      if (data.kind === 'waste' && data.reason === undefined) return problem(400, 'validation_failed', 'Waste needs a reason.');
      if (data.kind !== 'waste' && data.reason !== undefined) return problem(400, 'validation_failed', 'Only waste has a reason.');
      return idempotent(key, input, () => {
        const takes: { item: ItemRecord; quantity: number; productId: string | null }[] = [];
        for (const line of data.lines) {
          const byItem = line.stockItemId !== undefined;
          const byDish = line.productId !== undefined;
          if (byItem === byDish) return problem(400, 'validation_failed', 'A line names an item or a dish, not both.');
          if (byItem) {
            const item = findItem(venueId, line.stockItemId ?? '');
            if (item === undefined || item.archived) return notFound('No such item at this venue.');
            if (allowed.narrow && item.kind !== 'supply') return forbidden();
            const quantity = quantityOf(item, line.quantity, line.packs);
            if (typeof quantity !== 'number') return quantity;
            if (quantity <= 0) return problem(400, 'validation_failed', 'Each line takes something away.');
            takes.push({ item, quantity, productId: null });
            continue;
          }
          if (allowed.narrow) return forbidden();
          const servings = line.servings ?? 1;
          const own = recipesAt(venueId).filter((record) => record.productId === line.productId);
          const base = own.find((record) => record.variantId === null && record.modifierId === null);
          const variant = line.variantId === undefined ? undefined : own.find((record) => record.variantId === line.variantId);
          const main = variant ?? base;
          if (main === undefined) return problem(409, 'conflict', 'Conflict', { detail: 'That dish has no recipe here.' });
          const extras = own.filter((record) => record.modifierId !== null && (line.modifierIds ?? []).some((chosen) => chosen === record.modifierId));
          for (const record of [main, ...extras]) {
            for (const component of record.components) {
              const item = findItem(venueId, component.stockItemId);
              if (item !== undefined) takes.push({ item, quantity: component.quantity * servings, productId: line.productId ?? null });
            }
          }
        }
        const movements = takes.map((take) =>
          move(venueId, take.item, data.kind, -take.quantity, {
            reason: data.kind === 'waste' ? (data.reason ?? null) : null,
            note: data.note ?? null,
            productId: take.productId,
            stationId: data.kind === 'issue' ? (data.stationId ?? null) : null,
          }),
        );
        return json(201, { movements: movements.map(movementDto) });
      });
    }

    if (rest === '/adjustments' && method === 'POST') {
      const allowed = allow('inventory:adjust', true);
      if ('status' in allowed) return allowed;
      if (!enabledHere) return off();
      const parsed = CreateStockAdjustmentSchema.safeParse(input);
      if (!parsed.success) return invalid(parsed.error);
      return idempotent(key, input, () => {
        const item = findItem(venueId, parsed.data.stockItemId);
        if (item === undefined) return notFound('No such item at this venue.');
        if (allowed.narrow && item.kind !== 'supply') return forbidden();
        return json(201, movementDto(move(venueId, item, 'adjust', parsed.data.quantity, { note: parsed.data.note })));
      });
    }

    if (rest === '/counts') {
      if (method === 'GET') {
        const refused = read();
        if (refused !== undefined) return refused;
        const status = searchParams.get('status');
        const rows = state.counts
          .filter((count) => status === null || count.status === status)
          .sort((a, b) => (a.status === 'open' ? -1 : 0) - (b.status === 'open' ? -1 : 0) || b.startedAt.localeCompare(a.startedAt))
          .map((count) => countDto(venueId, count, false));
        return json(200, page(rows, searchParams));
      }
      if (method === 'POST') {
        const allowed = allow('inventory:count', true);
        if ('status' in allowed) return allowed;
        if (!enabledHere) return off();
        const parsed = CreateStockCountSchema.safeParse(input);
        if (!parsed.success) return invalid(parsed.error);
        const active = itemsAt(venueId).filter((item) => !item.archived);
        let chosen: ItemRecord[];
        let kind = parsed.data.kind;
        if (kind === 'full' && !allowed.narrow) chosen = active;
        else if (kind === 'full') {
          // With supplies alone, a count is a spot count of the supplies.
          kind = 'spot';
          chosen = active.filter((item) => item.kind === 'supply');
        } else {
          const ids = parsed.data.stockItemIds ?? [];
          if (ids.length === 0) return problem(400, 'validation_failed', 'A spot count names its items.');
          const found = ids.map((one) => active.find((item) => item.id === one));
          if (found.some((item) => item === undefined)) return notFound('No such item at this venue.');
          chosen = found.filter((item): item is ItemRecord => item !== undefined);
          if (allowed.narrow && chosen.some((item) => item.kind !== 'supply')) return forbidden();
        }
        const record: CountRecord = {
          id: fresh('skc'),
          kind,
          status: 'open',
          note: parsed.data.note ?? null,
          startedAt: INVENTORY_NOW,
          startedByName: RECORDER,
          submittedAt: null,
          submittedByName: null,
          cancelledAt: null,
          lines: chosen.map((item) => ({ stockItemId: item.id, counted: null, expected: null, variance: null, varianceValue: null, entered: null, countedAt: null })),
          varianceValue: null,
          submitNote: null,
        };
        state.counts.unshift(record);
        return json(201, countDto(venueId, record));
      }
      return undefined;
    }

    const countPath = /^\/counts\/([^/]+)(\/lines|\/submit|\/cancel)?$/.exec(rest);
    if (countPath !== null) {
      const record = state.counts.find((one) => one.id === countPath[1]);
      const action = countPath[2];
      if (action === undefined && method === 'GET') {
        const refused = read();
        if (refused !== undefined) return refused;
        return record === undefined ? notFound('No such count at this venue.') : json(200, countDto(venueId, record));
      }
      const allowed = allow('inventory:count', true);
      if ('status' in allowed) return allowed;
      if (!enabledHere) return off();
      if (record === undefined) return notFound('No such count at this venue.');
      if (record.status !== 'open') return problem(409, 'conflict', 'Conflict', { detail: 'That count is no longer open.' });
      if (action === '/lines' && method === 'PUT') {
        const parsed = PutStockCountLinesSchema.safeParse(input);
        if (!parsed.success) return invalid(parsed.error);
        for (const line of parsed.data.lines) {
          const item = findItem(venueId, line.stockItemId);
          if (item === undefined) return notFound('No such item at this venue.');
          if (allowed.narrow && item.kind !== 'supply') return forbidden();
          let entry = record.lines.find((one) => one.stockItemId === line.stockItemId);
          if (entry === undefined) {
            if (record.kind === 'full') return notFound('That item is not on this count.');
            entry = { stockItemId: item.id, counted: null, expected: null, variance: null, varianceValue: null, entered: null, countedAt: null };
            record.lines.push(entry);
          }
          if (line.clear === true) {
            Object.assign(entry, { counted: null, expected: null, entered: null, countedAt: null });
            continue;
          }
          const quantity = quantityOf(item, line.quantity, line.packs);
          if (typeof quantity !== 'number') return quantity;
          // Snapshot what was on hand as the shelf was counted, and keep what was typed.
          entry.counted = quantity;
          entry.expected = item.balance.quantity;
          entry.countedAt = INVENTORY_NOW;
          entry.entered = {
            quantity: line.quantity ?? 0,
            packs: (line.packs ?? []).map((one) => {
              const pack = item.packs.find((candidate) => candidate.id === one.packId);
              return { packId: one.packId, name: pack?.name ?? 'Pack', quantity: pack?.quantity ?? 1, count: one.count };
            }),
          };
        }
        return json(200, countDto(venueId, record));
      }
      if (action === '/submit' && method === 'POST') {
        const note = typeof input['note'] === 'string' && input['note'].trim() !== '' ? input['note'].trim() : null;
        return idempotent(key, { countId: record.id, note }, () => {
          const uncounted = record.lines.filter((line) => line.counted === null);
          if (record.kind === 'full' && uncounted.length > 0) {
            const names = uncounted.map((line) => findItem(venueId, line.stockItemId)?.name ?? 'Item');
            return problem(409, 'stock_count_incomplete', 'Some items have not been counted', { detail: `Still to count: ${names.join(', ')}.` });
          }
          for (const line of record.lines) {
            const item = findItem(venueId, line.stockItemId);
            if (item === undefined || line.counted === null) continue;
            // The variance is against what was on hand when the shelf was counted; what moved since still counts.
            const expected = line.expected ?? item.balance.quantity;
            const variance = line.counted - expected;
            line.expected = expected;
            line.variance = variance;
            if (variance !== 0) {
              const movement = move(venueId, item, 'count', variance, { countId: record.id });
              line.varianceValue = unitCostOf(item.balance) === null && movement.value === 0 ? null : movement.value;
            } else line.varianceValue = 0;
            item.lastCountedAt = INVENTORY_NOW;
          }
          if (record.kind === 'spot') record.lines = record.lines.filter((line) => line.counted !== null);
          record.status = 'submitted';
          record.submitNote = note;
          record.submittedAt = INVENTORY_NOW;
          record.submittedByName = RECORDER;
          record.varianceValue = record.lines.reduce((sum, line) => sum + (line.varianceValue ?? 0), 0);
          if (record.kind === 'full') state.lastFullCountAt = INVENTORY_NOW;
          return json(200, countDto(venueId, record));
        });
      }
      if (action === '/cancel' && method === 'POST') {
        record.status = 'cancelled';
        record.cancelledAt = INVENTORY_NOW;
        return json(200, countDto(venueId, record));
      }
      return undefined;
    }

    if (rest === '/recipes' && method === 'GET') {
      const refused = read();
      if (refused !== undefined) return refused;
      return json(200, { recipes: recipesAt(venueId).map((record) => recipeDto(venueId, record)) });
    }

    const productPath = /^\/products\/([^/]+)\/recipes$/.exec(rest);
    if (productPath !== null) {
      const product = COMMERCE_PRODUCTS.find((one) => one.id === productPath[1]);
      const retail = itemsAt(venueId).find((item) => item.productId === productPath[1]);
      const answer = () =>
        json(200, {
          productId: productPath[1],
          productName: product?.name ?? retail?.name ?? 'Product',
          retailItemId: retail?.id ?? null,
          soldOutForStock: soldOutForStock(venueId, productPath[1] ?? ''),
          recipes: recipesAt(venueId)
            .filter((record) => record.productId === productPath[1])
            .map((record) => recipeDto(venueId, record)),
          units: unitsOf(venueId, productPath[1] ?? ''),
        });
      if (method === 'GET') {
        const refused = read();
        if (refused !== undefined) return refused;
        if (product === undefined && retail === undefined) return notFound('No such venue or product.');
        return answer();
      }
      if (method === 'PUT') {
        if (!can('inventory:manage')) return forbidden();
        if (!enabledHere) return off();
        if (product === undefined) return notFound('No such venue or product.');
        const parsed = PutStockProductRecipesSchema.safeParse(input);
        if (!parsed.success) return invalid(parsed.error);
        const targets = new Set<string>();
        const next: RecipeRecord[] = [];
        for (const entry of parsed.data.recipes) {
          if (entry.variantId !== undefined && !product.variants.some((variant) => variant.id === entry.variantId)) return notFound('No such size of that product.');
          if (entry.modifierId !== undefined && !product.modifierGroups.some((group) => group.modifiers.some((modifier) => modifier.id === entry.modifierId))) return notFound('No such extra on that product.');
          if (entry.variantId !== undefined && entry.modifierId !== undefined) return problem(400, 'validation_failed', 'A recipe is of the product, a size or an extra.');
          const target = `${entry.variantId ?? ''}|${entry.modifierId ?? ''}`;
          if (targets.has(target)) return problem(400, 'validation_failed', 'One recipe per size or extra.');
          targets.add(target);
          const seen = new Set<string>();
          for (const component of entry.components) {
            const item = findItem(venueId, component.stockItemId);
            if (item === undefined || item.archived) return notFound('No such item at this venue.');
            if (seen.has(component.stockItemId)) return problem(400, 'validation_failed', 'An item is in a recipe once.');
            seen.add(component.stockItemId);
          }
          const existing = recipesAt(venueId).find((record) => record.productId === product.id && record.variantId === (entry.variantId ?? null) && record.modifierId === (entry.modifierId ?? null));
          next.push({ id: existing?.id ?? fresh('rcp'), productId: product.id, variantId: entry.variantId ?? null, modifierId: entry.modifierId ?? null, components: entry.components.map((component) => ({ ...component })) });
        }
        state.recipes.set(venueId, [...recipesAt(venueId).filter((record) => record.productId !== product.id), ...next]);
        return answer();
      }
      return undefined;
    }

    if (rest === '/copy-from-venue' && method === 'POST') {
      if (!can('inventory:manage')) return forbidden();
      if (!enabledHere) return off();
      const parsed = CopyStockFromVenueSchema.safeParse(input);
      if (!parsed.success) return invalid(parsed.error);
      const source: string = parsed.data.sourceVenueId;
      if (source === venueId) return problem(409, 'conflict', 'Conflict', { detail: 'Copy from another venue, not this one.' });
      if (source !== VENUE_ID && source !== SECOND_VENUE_ID) return notFound('No such venue in this organisation.');
      const dryRun = parsed.data.dryRun === true;
      if (!dryRun) state.copies.push({ sourceVenueId: source });
      // Definitions only, idempotently (ADR-0040 §9): a retail item is never copied (each venue links its own); an
      // active item here of the same name (ignoring case) and unit is matched, not duplicated; the same name in another
      // unit is skipped; one copied before is left as it is. Never a quantity, a cost or a movement.
      const mapping = new Map<string, string>();
      const items: { name: string; kind: StockItemKind; baseUnit: StockBaseUnit; outcome: 'copy' | 'match' | 'already_copied' | 'skip_retail' | 'skip_unit'; stockItemId: string | null }[] = [];
      const made: ItemRecord[] = [];
      const here = () => [...itemsAt(venueId), ...made].filter((one) => !one.archived);
      for (const item of itemsAt(source).filter((one) => !one.archived)) {
        const row = { name: item.name, kind: item.kind, baseUnit: item.baseUnit };
        if (item.kind === 'retail') {
          // A recipe that uses it uses this venue's own item for the same product.
          const ours = here().find((one) => one.kind === 'retail' && one.productId === item.productId);
          if (ours !== undefined) mapping.set(item.id, ours.id);
          items.push({ ...row, outcome: 'skip_retail', stockItemId: null });
          continue;
        }
        const already = here().find((one) => one.copiedFrom === item.id);
        if (already !== undefined) {
          mapping.set(item.id, already.id);
          items.push({ ...row, outcome: 'already_copied', stockItemId: already.id });
          continue;
        }
        const sameName = here().find((one) => one.name.trim().toLowerCase() === item.name.trim().toLowerCase());
        if (sameName !== undefined && sameName.baseUnit === item.baseUnit) {
          mapping.set(item.id, sameName.id);
          items.push({ ...row, outcome: 'match', stockItemId: sameName.id });
          continue;
        }
        if (sameName !== undefined) {
          items.push({ ...row, outcome: 'skip_unit', stockItemId: null });
          continue;
        }
        const copy: ItemRecord = {
          ...item,
          id: dryRun ? `preview-${item.id}` : fresh('ski'),
          packs: item.packs.filter((pack) => !pack.archived).map((pack) => ({ ...pack, id: dryRun ? `preview-${pack.id}` : fresh('skp') })),
          supplierId: null,
          balance: { quantity: 0, value: 0, basis: null },
          lastMovementAt: null,
          lastCountedAt: null,
          createdAt: INVENTORY_NOW,
          updatedAt: INVENTORY_NOW,
          copiedFrom: item.id,
        };
        mapping.set(item.id, copy.id);
        made.push(copy);
        items.push({ ...row, outcome: 'copy', stockItemId: dryRun ? null : copy.id });
      }
      const recipes: { productId: string; variantId: string | null; modifierId: string | null; name: string; outcome: 'copy' | 'already_here' | 'skip' }[] = [];
      const recipesMade: RecipeRecord[] = [];
      for (const record of recipesAt(source)) {
        const row = { productId: record.productId, variantId: record.variantId, modifierId: record.modifierId, name: recipeName(record) };
        const exists = recipesAt(venueId).some((one) => one.productId === record.productId && one.variantId === record.variantId && one.modifierId === record.modifierId);
        if (exists) {
          recipes.push({ ...row, outcome: 'already_here' });
          continue;
        }
        const components = record.components
          .map((component) => ({ stockItemId: mapping.get(component.stockItemId), quantity: component.quantity }))
          .filter((component): component is { stockItemId: string; quantity: number } => component.stockItemId !== undefined);
        if (components.length === 0) {
          recipes.push({ ...row, outcome: 'skip' });
          continue;
        }
        recipesMade.push({ ...record, id: dryRun ? `preview-${record.id}` : fresh('rcp'), components });
        recipes.push({ ...row, outcome: 'copy' });
      }
      if (!dryRun) {
        state.items.set(venueId, [...itemsAt(venueId), ...made]);
        state.recipes.set(venueId, [...recipesAt(venueId), ...recipesMade]);
      }
      const count = (outcome: string) => items.filter((one) => one.outcome === outcome).length;
      return json(200, {
        dryRun,
        itemsCopied: count('copy'),
        itemsMatched: count('match'),
        itemsAlreadyHere: count('already_copied'),
        itemsSkipped: count('skip_retail') + count('skip_unit'),
        recipesCopied: recipes.filter((one) => one.outcome === 'copy').length,
        recipesAlreadyHere: recipes.filter((one) => one.outcome === 'already_here').length,
        recipesSkipped: recipes.filter((one) => one.outcome === 'skip').length,
        items,
        recipes,
      });
    }

    const reportPath = /^\/reports\/(valuation|usage|variance|margins)$/.exec(rest);
    if (reportPath !== null && method === 'GET') {
      if (!can('inventory:reports')) return forbidden();
      const items = itemsAt(venueId).filter((item) => !item.archived);
      if (reportPath[1] === 'valuation') {
        const lines = items.map((item) => ({ stockItemId: item.id, name: item.name, kind: item.kind, baseUnit: item.baseUnit, onHand: item.balance.quantity, averageUnitCost: unitCostOf(item.balance), value: inr(item.balance.value) }));
        return json(200, { currency: CURRENCY, total: inr(lines.reduce((sum, line) => sum + line.value.amount, 0)), asOf: INVENTORY_NOW, items: lines });
      }
      if (reportPath[1] === 'margins') {
        const lines: unknown[] = [];
        for (const product of COMMERCE_PRODUCTS) {
          const own = recipesAt(venueId).filter((record) => record.productId === product.id && record.modifierId === null);
          if (own.length === 0) continue;
          const base = own.find((record) => record.variantId === null);
          const targets = product.variants.length === 0 ? [{ variantId: null, name: product.name, price: product.price, record: base }] : product.variants.map((variant) => ({ variantId: variant.id, name: `${product.name} - ${variant.name}`, price: variant.price, record: own.find((record) => record.variantId === variant.id) ?? base }));
          for (const target of targets) {
            if (target.record === undefined) continue;
            const priced = priceFoodOrderLine({ unitPrice: target.price, modifierPrices: [], quantity: 1, taxRateBps: product.taxRateBps }, TREATMENT);
            const { cost, complete } = costOf(venueId, target.record.components);
            const margin = priced.taxable.amount - cost;
            lines.push({
              productId: product.id,
              variantId: target.variantId,
              name: target.name,
              price: target.price,
              priceExclTax: priced.taxable,
              cost: inr(cost),
              costComplete: complete,
              margin: inr(margin),
              marginBps: priced.taxable.amount === 0 ? null : round(BigInt(margin) * 10_000n, BigInt(priced.taxable.amount)),
            });
          }
        }
        return json(200, { currency: CURRENCY, asOf: INVENTORY_NOW, products: lines });
      }
      // A range of business days in the venue's time (06:00 cutoff, Asia/Kolkata): each day starts at 00:30 UTC.
      const from = searchParams.get('from') ?? '2026-09-20';
      const to = searchParams.get('to') ?? '2026-09-26';
      const startsAt = `${from}T00:30:00.000Z`;
      const end = new Date(`${to}T00:30:00.000Z`);
      end.setUTCDate(end.getUTCDate() + 1);
      const endsAt = end.toISOString();
      const window = { from, to, startsAt, endsAt };
      const inWindow = state.movements.filter((movement) => movement.venueId === venueId && movement.occurredAt >= startsAt && movement.occurredAt < endsAt);
      if (reportPath[1] === 'usage') {
        const byItem = new Map<string, Map<StockMovementKind, { quantity: number; value: number }>>();
        for (const movement of inWindow) {
          const kinds = byItem.get(movement.stockItemId) ?? new Map();
          const row = kinds.get(movement.kind) ?? { quantity: 0, value: 0 };
          row.quantity += movement.quantity;
          row.value += movement.value;
          kinds.set(movement.kind, row);
          byItem.set(movement.stockItemId, kinds);
        }
        const totals = new Map<StockMovementKind, number>();
        const lines = items
          .filter((item) => byItem.has(item.id))
          .map((item) => ({
            stockItemId: item.id,
            name: item.name,
            kind: item.kind,
            baseUnit: item.baseUnit,
            byKind: [...(byItem.get(item.id) ?? new Map<StockMovementKind, { quantity: number; value: number }>()).entries()].map(([kind, row]) => {
              totals.set(kind, (totals.get(kind) ?? 0) + row.value);
              return { kind, quantity: row.quantity, value: inr(row.value) };
            }),
          }));
        return json(200, { window, currency: CURRENCY, items: lines, totals: [...totals.entries()].map(([kind, value]) => ({ kind, value: inr(value) })) });
      }
      const counts = state.counts.filter((count) => count.status === 'submitted' && count.submittedAt !== null && count.submittedAt >= startsAt && count.submittedAt < endsAt);
      const perItem = new Map<string, { quantity: number; value: number }>();
      for (const count of counts) {
        for (const line of count.lines) {
          const row = perItem.get(line.stockItemId) ?? { quantity: 0, value: 0 };
          row.quantity += line.variance ?? 0;
          row.value += line.varianceValue ?? 0;
          perItem.set(line.stockItemId, row);
        }
      }
      return json(200, {
        window,
        currency: CURRENCY,
        counts: counts.map((count) => ({ countId: count.id, kind: count.kind, submittedAt: count.submittedAt, submittedByName: count.submittedByName, lineCount: count.lines.length, varianceValue: inr(count.varianceValue ?? 0) })),
        items: [...perItem.entries()]
          .filter(([, row]) => row.quantity !== 0)
          .map(([stockItemId, row]) => {
            const item = findItem(venueId, stockItemId);
            return { stockItemId, name: item?.name ?? 'Item', baseUnit: item?.baseUnit ?? 'each', varianceQuantity: row.quantity, varianceValue: inr(row.value) };
          }),
        total: inr(counts.reduce((sum, count) => sum + (count.varianceValue ?? 0), 0)),
      });
    }

    return notFound(`No fake route for ${method} ${pathname}`);
  }

  return { state, handle, soldOutForStock: (productIdValue: string) => soldOutForStock(VENUE_ID, productIdValue) };
}

export type FakeInventory = ReturnType<typeof createFakeInventory>;
